import { dispatcher, DispatcherEvents } from "./dispatcher";
import { MESSAGING_URL, Router } from "../router";
import { extend, hashToArray } from "./vanilla";
import { getEventsUrl } from "./helpers";
import * as logger from "./logger";
import { DAYS } from "../constants";

export type TNotificationType = "dvir" | "messaging" | "app" | "error" | "news" | "hos" | "system";
export const NEW_MESSAGE_NOTIFICATION_STRING = "Messages ({0} new)";
export const NEW_EVENT_NOTIFICATION_STRING = "Events ({0} new)";
export const UNCERTIFIED_HOS_NOTIFICATION_STRING = "You have uncertified HOS logs";
export const DVIR_UNSYNCED_INSPECTION_STRING = "Your inspection hasn't reached your fleet yet. It's saved on this device and will send automatically once a connection is available.";

export function isNotificationDismissedRecently(dismissedDate: string | undefined, now: Date): boolean {
    return !!dismissedDate && (new Date(dismissedDate).getTime() + DAYS) >= now.getTime();
}

interface INotification {
    message: string;
    type?: TNotificationType;
    critical?: boolean;
    isDismissible?: boolean;
    navigateTo?: string;
    action?: () => any;
    isTranslated?: boolean;
    hidden?: boolean;
    count?: number;
}

export interface INotificationWithId extends INotification {
    id: string;
}

// Should be refactored into a static class!
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class Notifications {
    private static readonly notifications: IIdDictionary<IIdDictionary<INotificationWithId>> = {};

    static get(notificationId: string, userId: string): INotificationWithId | null {
        let userNotifications = this.getUserNotificationsReference(userId);
        return userNotifications && userNotifications[notificationId] ? userNotifications[notificationId] : null;
    }

    static getAll(userId: string) {
        let userNotifications = this.getUserNotificationsReference(userId);
        return hashToArray<INotificationWithId>(userNotifications || {});
    }

    static getVisible(userId: string): INotificationWithId[] {
        return this.getAll(userId).filter(notification => !notification.hidden);
    }

    static getCritical(userId: string): INotificationWithId[]  {
        let notificationsArray = this.getAll(userId);
        return notificationsArray.filter(alert => alert.critical);
    }

    static hasCritical(userId: string): boolean {
        let notificationsArray = this.getAll(userId);
        return notificationsArray.some(alert => !!alert.critical && !alert.hidden);
    }

    static click(router: Router, notificationId: string, allowedToNavigate: boolean, userId: string) {
        if (!notificationId) {
            return;
        }

        if (notificationId.indexOf("#") === 0 && allowedToNavigate && router) {
            router.navigate(notificationId, true);
            return;
        }

        let notification = this.get(notificationId, userId);

        if (notification) {
            notification.action && notification.action();

            if (allowedToNavigate && router && notification.navigateTo) {
                router.navigate(notification.navigateTo, true);
            }

            if (!notification.critical) {
                this.hide(notificationId, userId);
            }
        }
    }

    static add(notificationId: string, notification: INotification, userId: string, silent = false) {
        if (!notification || !notificationId) {
            return;
        }

        let userNotificationsRef = this.getUserNotificationsReference(userId);
        if (!userNotificationsRef) {
            this.notifications[userId] = {};
            userNotificationsRef = this.notifications[userId];
        }
        userNotificationsRef[notificationId] = extend({ id: notificationId }, notification);
        !silent && dispatcher.trigger(DispatcherEvents.NotificationsChange);
    }

    static remove(notificationId: string, userId: string, silent = false) {
        let notificationsRef = this.getUserNotificationsReference(userId);
        if (notificationId && notificationsRef && Object.keys(notificationsRef).length && notificationsRef[notificationId]) {
            delete notificationsRef[notificationId];
            !silent && dispatcher.trigger(DispatcherEvents.NotificationsChange);
        }
    }

    static hide(notificationId: string, userId: string, silent = false) {
        let notification = this.get(notificationId, userId);
        if (notification) {
            notification.hidden = true;
            !silent && dispatcher.trigger(DispatcherEvents.NotificationsChange);
        }
    }

    static clear(includeCritical: boolean, userId: string) {
        let notifications = this.getAll(userId);
        if (!includeCritical) {
            notifications = notifications.filter(notification => !(notification.critical && !notification.isDismissible));
        }

        notifications.forEach(notification => {
            this.hide(notification.id, userId, true);
        });

        notifications.length && dispatcher.trigger(DispatcherEvents.NotificationsChange);
    }

    static count(userId: string): number {
        let count = 0;
        this.getAll(userId).forEach(notification => {
            if (!notification.hidden) {
                count += notification.count || 1;
            }
        });
        return count;
    }

    static deepClear(includeCritical: boolean, userId: string) {
        this.clear(includeCritical, userId);
        if (userId in this.notifications) {
            delete this.notifications[userId];
        }
    }

    static updateUnreadEventsNotification(unreadEventsCount: number, currentUserId: string, translatedMessage?: string) {
        if (unreadEventsCount > 0) {
            let messageString = (translatedMessage || NEW_EVENT_NOTIFICATION_STRING).replace("{0}", unreadEventsCount.toString());

            Notifications.add("unreadEvents", {
                type: "system",
                message: messageString,
                critical: false,
                navigateTo: getEventsUrl(),
                isTranslated: true,
                count: unreadEventsCount
            }, currentUserId);
        } else {
            Notifications.remove("unreadEvents", currentUserId);
        }
    }

    static updateUnreadMessagesNotification(unreadMessagesCount: number, currentUserId: string, translatedMessage?: string) {
        if (unreadMessagesCount > 0) {
            let messageString = (translatedMessage || NEW_MESSAGE_NOTIFICATION_STRING).replace("{0}", unreadMessagesCount.toString());

            Notifications.add("unreadMessages", {
                type: "messaging",
                message: messageString,
                critical: false,
                navigateTo: MESSAGING_URL,
                isTranslated: true,
                count: unreadMessagesCount
            }, currentUserId);
        } else {
            Notifications.remove("unreadMessages", currentUserId);
        }
    }

    // Adds the uncertified HOS logs bell notification for the given driver.
    // onDismiss is called when the driver clicks the notification, allowing the caller to record the dismissal timestamp.
    static addUncertifiedHosLogsNotification(currentUserId: string, user: unknown, translatedMessage?: string, onDismiss?: () => void) {
        let isNew = !Notifications.get("uncertifiedHosLogs", currentUserId);
        Notifications.add("uncertifiedHosLogs", {
            type: "hos",
            message: translatedMessage || UNCERTIFIED_HOS_NOTIFICATION_STRING,
            critical: false,
            navigateTo: "hos/logs",
            isTranslated: true,
            action: () => {
                logger.log(logger.Source.USER_EVENT, "Uncertified HOS notification clicked", user);
                onDismiss && onDismiss();
            }
        }, currentUserId);
        if (isNew) {
            logger.log(logger.Source.USER_EVENT, "Uncertified HOS notification shown", user);
        }
    }


    private static getUserNotificationsReference(userId: string) {
        let userNotifications = this.notifications[userId] || null;
        return userNotifications;
    }
}